import React from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useModerationContext from '../../../../../context/useModerationContext';
import { ModerationAttendee, ModerationTransitionValidation } from '../../../../../models';
import { TransitionActionHandler } from '../../../flow/statusesTransitions/actions/handler/ActionsHandler';

import { Button, Modal, Section, SelectLabel, TitleHeader, ToastContext, ToggleLabel, Status } from '@weezevent/nacre';

import { capitalizeFirstLetter, translateLocale } from '../../../../attendee/detail/components/utils';
import { colors } from '../../../../../constants/moderation';
import { ActionComment } from './components/Comments';
import { Validations } from './components/Validations';
import { Loader } from 'semantic-ui-react';

import Style from './moderationModal.module.css';

export default function ModerationModal({ open, setOpenModal, attendee, organization, flowId, handleNextAttendee, nextAttendeeLoading, transitions, loadingTransitions, order }) {
    const locale = i18n.language;
    const history = useHistory();
    const { t } = useTranslation();
    const [{ flow, removedAttendeesQuery }] = useModerationContext();
    const toast = React.useContext(ToastContext);

    const [transitionId, setTransitionId] = React.useState(null);
    const [transitionActions, setTransitionActions] = React.useState([]);
    const [actionIds, setActionIds] = React.useState([]);
    const [validationsInputs, setValidationsInputs] = React.useState([]);
    const [commentAdded, setCommentAdded] = React.useState([]);

    const [transitionValidations, loadingTransitionValidations] = ModerationTransitionValidation.useApiModel(
        {
            organizationId: organization.pk,
            flowId,
            transitionId,
            launch: Boolean(transitionId),
            onSuccess: response => {
                setValidationsInputs(response.map(it => ({ ...it.object, value: false })));
            }
        },
        [organization, flowId, transitionId]
    );

    const transitionsOptions = React.useMemo(() => {
        if (!transitions || loadingTransitions) {
            return [];
        }

        return transitions?.map(transition => {
            const transitionName = transition.name ? transition.name : `${transition.source_status.name} > ${transition.destination_status.name}`;

            return {
                text: (
                    <div className={Style['moderation-modal-transitions-select-option']}>
                        {transitionName} <Status color={colors[transition.destination_status.stage]} text={transition.destination_status.name} />
                    </div>
                ),
                id: transition.id,
                key: transition.id,
                value: transition.id,
                actions: transition.actions,
                disabled: !transition.is_available
            };
        });
    }, [transitions, loadingTransitions]);

    const hasPendingValidations = React.useMemo(() => {
        if (!transitionId || loadingTransitionValidations || !validationsInputs.length > 0) {
            return false;
        }
        return validationsInputs.some(it => it.checkbox_mandatory && !it.value);
    }, [transitionId, loadingTransitionValidations, validationsInputs]);

    const isActive = action => actionIds.includes(action.id);

    const unfilledRequiredComments = React.useMemo(() => {
        return transitionActions.some(action => action.type === 'email_action' && action.comment_required && actionIds.includes(action.id) && !action.comment);
    }, [transitionActions, actionIds, commentAdded]);

    const processEmailActionsComments = (emailActions, actions) => {
        emailActions.forEach(emailAction => {
            //set comment payload only on activated email action
            const emailActionIndex = actionIds.findIndex(actionId => actionId === emailAction.id);
            if (emailActionIndex >= 0) {
                const _comment = emailAction.comment;

                if (typeof _comment === 'string') {
                    actions[emailActionIndex].data = {
                        comment: _comment
                    };
                }
                if (typeof _comment === 'object') {
                    const { id, label, comment } = _comment;
                    actions[emailActionIndex].data = {
                        id,
                        label,
                        comment
                    };
                }
            }
        });
    };

    const handleSubmit = React.useCallback(
        (isNextAttendeeCta = false) => {
            const actions = actionIds.map(id => ({ id }));
            let validations = validationsInputs.map(({ id, value }) => ({ id, value }));
            const emailActionsToTrigger = transitionActions.filter(action => action.type === 'email_action' && actionIds.includes(action.id));
            if (emailActionsToTrigger.length > 0) processEmailActionsComments(emailActionsToTrigger, actions);

            ModerationAttendee.triggerTransition(
                {
                    transition: transitionId,
                    actions,
                    validations
                },
                {
                    organizationId: organization.id,
                    attendeeId: attendee.id,
                    flow,
                    query: attendee?.removed ? removedAttendeesQuery : {}
                }
            )
                .then(() => {
                    if (isNextAttendeeCta) {
                        handleNextAttendee();
                    } else {
                        setTransitionActions([]);
                        if (flowId) {
                            history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/moderation_attendees`);
                        } else {
                            history.push(`/ticket/O${organization.id}/moderation/moderation_attendees`);
                        }
                    }
                    toast.success(t(`ticket.moderation.attendee.moderationModal.toastSuccess`));
                })
                .catch(_ => {
                    toast.error(t(`ticket.moderation.attendee.moderationModal.toastError`));
                });
        },
        [flow, removedAttendeesQuery, organization.id, attendee, flow, transitionId, validationsInputs, actionIds, transitionActions]
    );

    if (!open) {
        return null;
    }

    return (
        <Modal
            size="large"
            className={Style['moderation-modal']}
            open={open}
            onClose={() => {
                setTransitionActions([]);
                setOpenModal(false);
            }}
            title={
                <div className={Style['moderation-modal-titleContainer']}>
                    {t('ticket.moderation.attendee.moderationModal.title')}
                    <Status color={colors[attendee?.status.stage]} text={attendee?.status.name} />
                </div>
            }
            subtitle={attendee.attendeeResolvedName}
            allowExternalClosing={false}
            customFooter={
                <Button.Group className={Style['moderation-modal-groupContainer']}>
                    <Button label={t('ticket.common.action.back')} onClick={() => setOpenModal(false)} disabled={nextAttendeeLoading} />
                    <Button
                        primary
                        inverted
                        label={t('ticket.moderation.attendee.moderationModal.ctaSaveQuit')}
                        onClick={() => handleSubmit()}
                        disabled={hasPendingValidations || unfilledRequiredComments || nextAttendeeLoading}
                    />
                    <Button
                        primary
                        label={
                            nextAttendeeLoading ? <Loader className={Style['loader-count-loader']} active /> : t('ticket.moderation.attendee.moderationModal.saveNextAttendeeCTA')
                        }
                        disabled={hasPendingValidations || unfilledRequiredComments}
                        onClick={() => handleSubmit(true)}
                    />
                </Button.Group>
            }
        >
            <SelectLabel
                useNew
                label={t('ticket.moderation.attendee.moderationModal.transitionLabel')}
                value={transitionId}
                options={transitionsOptions}
                placeholder={t('ticket.common.action.select')}
                className={Style['moderation-modal-transitionSelect']}
                onChange={(_, { id, actions }) => {
                    setTransitionId(id);
                    setTransitionActions(actions);
                    setActionIds(actions.filter(action => action.optional_value).map(action => action.id));
                }}
            />
            {transitionActions?.length > 0 && (
                <>
                    {transitionActions.map((action, i) => (
                        <Section title={'test'} key={i}>
                            <TitleHeader title={`Action ${i + 1} : ${action.name}`} />

                            {action.type === 'email_action' && (
                                <ActionComment locale={order?.locale} action={action} isActive={isActive(action)} setCommentAdded={setCommentAdded} />
                            )}
                            {transitionId && (
                                <Validations validationsInputs={validationsInputs} setValidationsInputs={setValidationsInputs} transitionValidations={transitionValidations} />
                            )}
                            {TransitionActionHandler.get_allowed_actions(organization.id).includes(action.type) && (
                                <ToggleLabel
                                    className={Style['moderation-modal-notify']}
                                    inlinePosition="left"
                                    label={`${t(`ticket.moderation.attendee.moderationModal.actions.${action.type}`)}`}
                                    checked={action.optional_value || action.optional_value === null}
                                    disabled={action.optional_value === null}
                                    onChange={(_, { checked }) => {
                                        if (checked) {
                                            setActionIds(prev => [...prev, action.id]);
                                        } else {
                                            setActionIds(prev => prev.filter(id => id !== action.id));
                                        }
                                    }}
                                />
                            )}
                        </Section>
                    ))}
                </>
            )}
            <p>
                {t('ticket.moderation.attendee.moderationModal.languageLabel')}
                <b>
                    {capitalizeFirstLetter(translateLocale(locale, order?.locale.split('_')[1]))} ({order?.locale.split('_')[1].toUpperCase()})
                </b>
            </p>
        </Modal>
    );
}
